import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FileSpreadsheet, FileText, Loader2, Upload, X, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDocumentsStore } from '@/stores/documents-store'
import { isWorkbook, validateWorkbookFile } from '@/lib/workbook'
import { isWordFormSource, validateWordFormFile } from '@/lib/word-form'
import { cn } from '@/lib/utils'

/** What kind of file the picker offers: a workbook (Excel schedule) or a Word form. */
export type ScheduleSourceKind = 'workbook' | 'word'

interface SourceKindConfig {
  label: string
  loading: string
  placeholder: string
  uploadFailed: string
  dropText: string
  uploadLabel: string
  footnote: string
  icon: LucideIcon
  isSource: (filename: string, mimeType: string) => boolean
  validate: (file: File) => string | null
}

const SOURCE_KINDS: Record<ScheduleSourceKind, SourceKindConfig> = {
  workbook: {
    label: 'Workbook',
    loading: 'Loading workbooks…',
    placeholder: 'Choose an uploaded workbook',
    uploadFailed: 'That workbook could not be uploaded. Please try again.',
    dropText: 'Drop an .xlsx or .xlsm here, or browse',
    uploadLabel: 'Upload a workbook',
    footnote: 'We copy the workbook, so the original upload is never changed.',
    icon: FileSpreadsheet,
    isSource: isWorkbook,
    validate: validateWorkbookFile,
  },
  word: {
    label: 'Word form',
    loading: 'Loading Word files…',
    placeholder: 'Choose an uploaded Word file',
    uploadFailed: 'That Word file could not be uploaded. Please try again.',
    dropText: 'Drop a .docx here, or browse',
    uploadLabel: 'Upload a Word file',
    footnote: "We fill a copy, so the buyer's file is never changed.",
    icon: FileText,
    isSource: isWordFormSource,
    validate: validateWordFormFile,
  },
}

/** Everything the dialog needs to know about the picker, in one object. */
export interface ScheduleSourceState {
  /** Selected document id, '' when nothing is selected. */
  documentId: string
  /** Selected document's filename, '' when nothing is selected. */
  filename: string
  /** The document THIS picker created, so the dialog can clean it up. */
  uploadedDocId: string | null
  /** True while an upload is in flight — the dialog blocks Create on it. */
  isUploading: boolean
}

export const EMPTY_SCHEDULE_SOURCE_STATE: ScheduleSourceState = {
  documentId: '',
  filename: '',
  uploadedDocId: null,
  isUploading: false,
}

export interface ScheduleSourcePickerProps {
  bidId: string
  value: string
  onChange: (state: ScheduleSourceState) => void
  disabled?: boolean
  /** Defaults to a workbook (Excel schedule). */
  kind?: ScheduleSourceKind
}

/**
 * Lets the user end up with a valid `sourceDocumentId` for this bid, either by
 * choosing a workbook already on the bid or by uploading one here.
 *
 * Deliberately does NOT reuse `useBidDocumentUpload`: that hook interposes
 * `DuplicateUploadDialog` on a name collision, and stacking a dialog on a dialog
 * is bad. Re-uploading a schedule under the same name is legitimate anyway — a
 * revised pricing sheet — so this calls `uploadTenderDocuments` directly.
 */
export function ScheduleSourcePicker({
  bidId,
  value,
  onChange,
  disabled = false,
  kind = 'workbook',
}: ScheduleSourcePickerProps) {
  const copy = SOURCE_KINDS[kind]
  const SourceIcon = copy.icon
  const documents = useDocumentsStore((s) => s.documents)
  const documentsBidId = useDocumentsStore((s) => s.currentBidId)
  const uploadingFiles = useDocumentsStore((s) => s.uploadingFiles)
  const uploadTenderDocuments = useDocumentsStore((s) => s.uploadTenderDocuments)
  const deleteDocument = useDocumentsStore((s) => s.deleteDocument)

  const [error, setError] = useState<string | null>(null)
  const [uploadingName, setUploadingName] = useState<string | null>(null)
  const [uploadedDocId, setUploadedDocId] = useState<string | null>(null)
  const [uploadedName, setUploadedName] = useState('')
  /**
   * Documents this picker has asked the server to delete. `deleteDocument` is
   * async, so without this the store still lists a removed workbook for a beat —
   * long enough for the sole-workbook effect below to re-select the very document
   * being deleted, leaving `value` pointing at a row that no longer exists.
   */
  const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(new Set())

  const isLoadingDocuments = documentsBidId !== bidId

  const sources = useMemo(
    () =>
      documents
        .filter(
          (doc) =>
            doc.bidId === bidId &&
            !removedIds.has(doc.id) &&
            copy.isSource(doc.name, doc.mimeType)
        )
        .map((doc) => ({ id: doc.id, name: doc.name })),
    [documents, bidId, removedIds, copy]
  )

  const progress =
    uploadingFiles.find((f) => f.file.name === uploadingName)?.progress ?? 0

  const handleSelect = (documentId: string) => {
    const source = sources.find((s) => s.id === documentId)
    setError(null)
    onChange({
      documentId,
      filename: source?.name ?? '',
      uploadedDocId: null,
      isUploading: false,
    })
  }

  /**
   * Naming the response after the only workbook is right ~always — offer it.
   *
   * Stands down during and after an upload. Starting an upload clears `value`,
   * which would otherwise re-trigger this effect and snap the selection back to
   * the workbook the user is in the middle of replacing — re-enabling Create
   * against the stale document, which is the exact bug `isUploading` exists to
   * prevent.
   */
  useEffect(() => {
    if (value || isLoadingDocuments || sources.length !== 1) return
    if (uploadingName !== null || uploadedDocId !== null) return
    const only = sources[0]!
    onChange({
      documentId: only.id,
      filename: only.name,
      uploadedDocId: null,
      isUploading: false,
    })
    // Keyed on the source set and the current selection only: including
    // `onChange` would re-fire on every parent render and fight its state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, value, isLoadingDocuments, uploadingName, uploadedDocId])

  const handleDrop = useCallback(
    async (accepted: File[]) => {
      const file = accepted[0]
      if (!file) return

      const validationError = copy.validate(file)
      if (validationError) {
        setError(validationError)
        return
      }

      setError(null)
      setUploadingName(file.name)
      onChange({ ...EMPTY_SCHEDULE_SOURCE_STATE, isUploading: true })

      const created = await uploadTenderDocuments(bidId, [file])
      setUploadingName(null)

      const doc = created[0]
      if (!doc) {
        setError(copy.uploadFailed)
        onChange(EMPTY_SCHEDULE_SOURCE_STATE)
        return
      }

      setUploadedDocId(doc.id)
      setUploadedName(doc.name)
      onChange({
        documentId: doc.id,
        filename: doc.name,
        uploadedDocId: doc.id,
        isUploading: false,
      })
    },
    [bidId, onChange, uploadTenderDocuments, copy]
  )

  /**
   * Best-effort and silent. `deleteDocument` sets a global store error and
   * rethrows; surfacing that on a dialog the user is dismissing is worse than
   * the orphan it reports. Worst case is one stray document, removable from the
   * Documents tab.
   */
  const handleRemove = useCallback(async () => {
    const docId = uploadedDocId
    // Hide it from `sources` before clearing the selection, or the sole-workbook
    // effect re-selects it during the delete round-trip.
    if (docId) setRemovedIds((prev) => new Set(prev).add(docId))
    setUploadedDocId(null)
    setUploadedName('')
    setError(null)
    onChange(EMPTY_SCHEDULE_SOURCE_STATE)
    if (!docId) return
    try {
      await deleteDocument(docId)
    } catch {
      // Intentionally swallowed — see above.
    }
  }, [deleteDocument, onChange, uploadedDocId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted: File[]) => void handleDrop(accepted),
    multiple: false,
    disabled: disabled || uploadingName !== null,
  })

  /**
   * With no workbook on the bid there is nothing to choose, and Radix renders an
   * empty `SelectContent` as a bare sliver of a popover. Drop the trigger (and
   * the "or" beneath it) so the empty case is just the dropzone.
   */
  const showSelect =
    !isLoadingDocuments && uploadedDocId === null && sources.length > 0

  return (
    <div className="space-y-2">
      <Label htmlFor="schedule-source">{copy.label}</Label>

      {isLoadingDocuments && <p className="text-meta">{copy.loading}</p>}

      {showSelect && (
        <Select value={value} onValueChange={handleSelect} disabled={disabled}>
          <SelectTrigger id="schedule-source" aria-label={copy.label}>
            <SelectValue placeholder={copy.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {sources.map((source) => (
              <SelectItem key={source.id} value={source.id}>
                {source.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {uploadedDocId !== null ? (
        <div className="flex items-center gap-2 rounded-lg border border-border p-3">
          <SourceIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="truncate text-xs">{uploadedName}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto h-6 w-6"
            aria-label={`Remove ${uploadedName}`}
            onClick={() => void handleRemove()}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : uploadingName !== null ? (
        <div className="flex items-center gap-2 rounded-lg border border-border p-3">
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground" />
          <span className="truncate text-xs">{uploadingName}</span>
          <Progress value={progress} className="ml-auto h-1 w-24" />
        </div>
      ) : (
        <>
          {showSelect && <p className="text-center text-meta">or</p>}
          <div
            {...getRootProps()}
            className={cn(
              'flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border p-4 transition-colors',
              isDragActive && 'border-[hsl(var(--brand))] bg-muted/30',
              disabled && 'pointer-events-none opacity-50'
            )}
          >
            <input {...getInputProps()} aria-label={copy.uploadLabel} />
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-meta">{copy.dropText}</span>
          </div>
        </>
      )}

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}

      <p className="text-meta">{copy.footnote}</p>
    </div>
  )
}
